import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import * as communityService from '../../services/community.service';
import MentionInput from '../../components/common/MentionInput';
import { renderRichText } from '../../utils/richText';
import MediaPicker, { mediaUploading, mediaPayload } from '../../components/hive/MediaPicker';
import MediaGrid from '../../components/hive/MediaGrid';
import ContestEntryBox from '../../components/hive/ContestEntryBox';
import CopyLinkButton from '../../components/hive/CopyLinkButton';
import ReactionBar from '../../components/dashboard/ReactionBar';
import TagBubbles from '../../components/admin/TagBubbles';
import api from '../../services/api';

export default function DiscussionPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  // Topic tags (Jenn, 2026-10-29): members tag their own posts and comments, Team365 any. Search finds them.
  const [hiveTags, setHiveTags] = useState({ post: {}, comment: {}, all: [] });

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState(null);
  const [error, setError] = useState(null);
  const [commentBody, setCommentBody] = useState('');
  const [commentMedia, setCommentMedia] = useState([]);
  const [contests, setContests] = useState([]);
  const [commentContest, setCommentContest] = useState('');
  const [replyContest, setReplyContest] = useState('');
  const [posting, setPosting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // comment id, or null
  const [replyBody, setReplyBody] = useState('');
  const [replyMedia, setReplyMedia] = useState([]);
  const [replying, setReplying] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await communityService.getPost(id);
      setPost(data.post);
      setComments(data.comments);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load this post.');
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Running members-only contests; on a contest's own Hive post the entry box starts ticked.
  useEffect(() => {
    communityService.getRunningContests().then(list => {
      setContests(list);
      const own = list.find(c => c.hivePostId === id);
      if (own) setCommentContest(own.id);
    }).catch(() => {});
  }, [id]);
  const ownContestId = contests.find(c => c.hivePostId === id)?.id || '';

  const submitComment = async (e) => {
    e.preventDefault();
    if ((!commentBody.trim() && !mediaPayload(commentMedia).length) || mediaUploading(commentMedia)) return;
    setPosting(true);
    try {
      await communityService.createCommentWithMedia(id, commentBody.trim(), undefined, mediaPayload(commentMedia), commentContest || null);
      setCommentBody('');
      setCommentMedia([]);
      setCommentContest(ownContestId);
      const data = await communityService.getPost(id);
      setComments(data.comments);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not post your comment.');
    } finally {
      setPosting(false);
    }
  };

  const removeComment = async (commentId) => {
    if (!window.confirm('Delete your comment?')) return;
    await communityService.deleteOwnComment(commentId);
    const data = await communityService.getPost(id);
    setComments(data.comments);
  };

  const reactToPost = async (type) => {
    try {
      const reactions = await communityService.reactToPost(id, type);
      setPost(prev => ({ ...prev, reactions }));
    } catch { /* non-critical */ }
  };

  const reactToComment = async (commentId, type) => {
    try {
      const reactions = await communityService.reactToComment(commentId, type);
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, reactions } : c));
    } catch { /* non-critical */ }
  };

  const submitReply = async (parentId) => {
    if ((!replyBody.trim() && !mediaPayload(replyMedia).length) || mediaUploading(replyMedia)) return;
    setReplying(true);
    try {
      await communityService.createCommentWithMedia(id, replyBody.trim(), parentId, mediaPayload(replyMedia), replyContest || null);
      setReplyBody('');
      setReplyMedia([]);
      setReplyContest('');
      setReplyingTo(null);
      const data = await communityService.getPost(id);
      setComments(data.comments);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add your reply.');
    } finally {
      setReplying(false);
    }
  };

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-sm text-red-700">
          {error}
          <button onClick={() => navigate('/dashboard/discussion')} className="block mt-2 underline">
            Back to My Hive
          </button>
        </div>
      </DashboardLayout>
    );
  }

  if (!post) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-sm text-gray-400">Loading…</div>
      </DashboardLayout>
    );
  }

  const dateLabel = new Date(post.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate('/dashboard/discussion')} className="text-xs text-gray-400 hover:text-reset-brown mb-4">
          ← My Hive
        </button>

        <article className="bg-white rounded-2xl shadow-sm overflow-hidden mb-6">
          {post.cover_image_url && (
            <img src={post.cover_image_url} alt={post.cover_image_alt || ''} className="w-full h-auto max-h-[85vh] object-contain bg-reset-creamDark" />
          )}
          <div className="p-6">
            <p className="text-xs text-gray-400 mb-2">
              {dateLabel} · {post.author?.name || 'The Reset Team'}
            </p>
            {post.title && <h1 className="font-serif text-2xl font-bold text-reset-brown mb-4">{post.title}</h1>}
            <div className="text-sm text-gray-700 leading-relaxed">{renderRichText(post.body)}</div>
            {post.image_url && (
              <img src={post.image_url} alt="" className="max-h-96 rounded-xl object-cover mt-4" />
            )}
            <MediaGrid media={post.media} />
            <div className="mt-3">
              <HiveTagLoader postId={post.id} commentIds={comments.map(c => c.id)} setHiveTags={setHiveTags} />
              <TagBubbles key={`${post.id}|${(hiveTags.post[post.id] || []).join(',')}`} kind="hive_post" id={post.id} endpoint="/community/tags"
                initial={hiveTags.post[post.id] || []} allTags={hiveTags.all} readOnly={!(isAdmin || post.member_id === user?.id)} />
            </div>
            <div className="mt-4 flex items-center justify-between gap-3">
              <ReactionBar reactions={post.reactions} onReact={reactToPost} />
              <CopyLinkButton postId={post.id} />
            </div>
          </div>
        </article>

        <h2 className="text-sm font-bold text-reset-brown mb-3">
          {comments.length} Comment{comments.length === 1 ? '' : 's'}
        </h2>

        <form onSubmit={submitComment} className="bg-white rounded-2xl shadow-sm p-4 mb-4">
          <MentionInput
            value={commentBody}
            onChange={setCommentBody}
            placeholder="Add a comment… (type @ to tag someone)"
            rows={2}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
          <div className="mt-2">
            <MediaPicker items={commentMedia} setItems={setCommentMedia} compact />
          </div>
          <ContestEntryBox contests={contests} value={commentContest} onChange={setCommentContest} />
          <div className="flex justify-end mt-2">
            <button
              type="submit"
              disabled={posting || (!commentBody.trim() && !mediaPayload(commentMedia).length) || mediaUploading(commentMedia)}
              className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
            >
              {posting ? 'Posting…' : mediaUploading(commentMedia) ? 'Uploading…' : 'Post comment'}
            </button>
          </div>
        </form>

        <div className="space-y-3">
          {comments.filter(c => !c.parent_comment_id).map(comment => (
            <div key={comment.id}>
              <CommentRow
                comment={comment}
                isOwn={comment.member_id === user?.id}
                canTag={isAdmin || comment.member_id === user?.id}
                tags={hiveTags.comment[comment.id] || []}
                allTags={hiveTags.all}
                onDelete={() => removeComment(comment.id)}
                onReply={() => { setReplyingTo(comment.id); setReplyBody(''); setReplyMedia([]); setReplyContest(ownContestId); setError(null); }}
                onReact={(type) => reactToComment(comment.id, type)}
              />

              {comments.filter(c => c.parent_comment_id === comment.id).map(reply => (
                <div key={reply.id} className="ml-8 mt-2">
                  <CommentRow
                    comment={reply}
                    isOwn={reply.member_id === user?.id}
                    canTag={isAdmin || reply.member_id === user?.id}
                    tags={hiveTags.comment[reply.id] || []}
                    allTags={hiveTags.all}
                    onDelete={() => removeComment(reply.id)}
                    onReply={() => { setReplyingTo(comment.id); setReplyBody(''); setReplyMedia([]); setReplyContest(ownContestId); setError(null); }}
                    onReact={(type) => reactToComment(reply.id, type)}
                  />
                </div>
              ))}

              {replyingTo === comment.id && (
                <div className="ml-8 mt-2 bg-white rounded-2xl shadow-sm p-3">
                  <MentionInput
                    value={replyBody}
                    onChange={setReplyBody}
                    placeholder="Write a reply… (type @ to tag someone)"
                    rows={2}
                    autoFocus
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                  />
                  <div className="mt-2">
                    <MediaPicker items={replyMedia} setItems={setReplyMedia} compact />
                  </div>
                  <ContestEntryBox contests={contests} value={replyContest} onChange={setReplyContest} />
                  <div className="flex justify-end gap-2 mt-2">
                    <button onClick={() => setReplyingTo(null)} className="text-xs text-gray-400 hover:text-reset-brown">Cancel</button>
                    <button
                      onClick={() => submitReply(comment.id)}
                      disabled={replying || (!replyBody.trim() && !mediaPayload(replyMedia).length) || mediaUploading(replyMedia)}
                      className="px-3 py-1.5 bg-reset-terracotta text-white text-xs font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
                    >
                      {replying ? 'Replying…' : 'Reply'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

/** Loads the topic tags for this post and its comments. Renders nothing. */
function HiveTagLoader({ postId, commentIds, setHiveTags }) {
  const ids = commentIds.join(',');
  useEffect(() => {
    let live = true;
    Promise.all([
      api.get('/community/tags', { params: { kind: 'hive_post', ids: postId } }),
      ids ? api.get('/community/tags', { params: { kind: 'hive_comment', ids } }) : Promise.resolve({ data: { tags: {} } }),
      api.get('/community/tags/all'),
    ]).then(([p, c, a]) => live && setHiveTags({ post: p.data.tags, comment: c.data.tags, all: a.data.tags })).catch(() => {});
    return () => { live = false; };
  }, [postId, ids, setHiveTags]);
  return null;
}

function CommentRow({ comment, isOwn, canTag, tags = [], allTags = [], onDelete, onReply, onReact }) {
  const initials = (comment.author?.name || '?').trim().charAt(0).toUpperCase();
  const dateLabel = new Date(comment.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 flex items-start gap-3">
      {comment.author?.avatar_url ? (
        <img src={comment.author.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-reset-sage text-white flex items-center justify-center text-xs font-bold shrink-0">
          {initials}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-reset-brown">{comment.author?.name || '(deleted member)'}</span>
          <span className="text-[11px] text-gray-400">{dateLabel}</span>
        </div>
        {comment.body && <div className="text-sm text-gray-700 mt-0.5 [&_p]:mb-0">{renderRichText(comment.body)}</div>}
        {comment.image_url && (
          <img src={comment.image_url} alt="" className="max-h-56 rounded-lg object-cover mt-2" />
        )}
        <MediaGrid media={comment.media} size="comment" />
        {comment.contest_entry && <p className="text-[11px] text-reset-terracotta font-medium mt-1">🏆 Contest entry: {comment.contest_entry}</p>}
        <div className="mt-1.5">
          <TagBubbles key={`${comment.id}|${tags.join(',')}`} kind="hive_comment" id={comment.id} endpoint="/community/tags"
            initial={tags} allTags={allTags} readOnly={!canTag} />
        </div>
        <div className="flex items-center gap-3 mt-1">
          <ReactionBar reactions={comment.reactions} onReact={onReact} />
          <button onClick={onReply} className="text-[11px] font-medium text-gray-400 hover:text-reset-terracotta">
            Reply
          </button>
        </div>
      </div>
      {isOwn && (
        <button onClick={onDelete} className="text-[11px] text-gray-300 hover:text-red-500 shrink-0">
          Delete
        </button>
      )}
    </div>
  );
}
